import { Avatar, Box, Button, Grid, Modal, Paper, Skeleton, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { fetchVirtualBadge } from '../../../api/virtualBadge'
import { useThemeMode } from '../../../theme'
import { avatarUrl, isDefined } from '../../../utils'

const getThemeSx = (palette) => ({
  button: {
    background: palette.paperColor,
    color: palette.headerColor,
    padding: '0px',
    borderRadius: 25,
    textTransform: 'none',
    '&:hover': {
      background: palette.paperColorDim
    },
    '& .MuiTypography-root': {
      color: palette.textColor,
      fontWeight: 600,
      paddingRight: '10px',
      fontSize: '0.75rem',
      '@media (min-width:400px)': {
        fontSize: '1rem'
      }
    }
  },
  avatar: {
    background: palette.paperColor,
    color: palette.headerColor
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .companion-virtual-badge-modal': {
      minWidth: '280px',
      maxWidth: '90vw',
      padding: '15px'
    },
    '& .companion-virtual-badge-content': {
      padding: '10px',
      textAlign: 'center'
    },
    '& .companion-virtual-badge-avatar-wrapper': {
      width: '45%',
      maxWidth: '220px',
      margin: '0 auto'
    },
    '& .companion-virtual-badge-avatar': {
      width: '100%',
      height: 'auto',
      aspectRatio: '1 / 1'
    },
    '& .companion-virtual-badge-qr': {
      display: 'flex',
      justifyContent: 'center',
      padding: '10px',
      marginTop: '5px'
    },
    '& .companion-virtual-badge-qr-placeholder': {
      width: '220px',
      maxWidth: '100%'
    },
    '& .companion-virtual-badge-qr-placeholder .MuiSkeleton-root': {
      width: '100%',
      height: '220px'
    },
    '& .companion-virtual-badge-qr img': {
      maxWidth: '220px',
      width: '100%'
    },
    '& .companion-virtual-badge-label': {
      color: palette.headerColor,
      fontWeight: 600,
      display: 'block'
    }
  }
})

export const VirtualBadge = (props) => {
  const { avatar_url, nick, attendee } = props
  const { t } = useTranslation()
  const { palette } = useThemeMode()
  const themeSx = getThemeSx(palette)
  const { config } = useSelector((state) => state?.config)
  const auth = useSelector((state) => state?.auth)
  const [open, setOpen] = useState(false)
  const [qrUrl, setQrUrl] = useState(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(undefined)

  const avatar = useMemo(
    () => avatarUrl(config, avatar_url),
    [config, avatar_url]
  )

  useEffect(() => {
    return () => {
      if (isDefined(qrUrl)) {
        URL.revokeObjectURL(qrUrl)
      }
    }
  }, [qrUrl])

  const fetchQr = () => {
    setLoading(true)
    setError(undefined)

    fetchVirtualBadge(
      config,
      auth,
      (response) => {
        if (isDefined(qrUrl)) {
          URL.revokeObjectURL(qrUrl)
        }
        const blobUrl = URL.createObjectURL(response.data)
        setQrUrl(blobUrl)
        setLoading(false)
      },
      () => {
        setError(t('internal-server-error'))
        setLoading(false)
      }
    )
  }

  const openModal = () => {
    setOpen(true)
    if (!isDefined(qrUrl)) {
      fetchQr()
    }
  }

  const closeModal = () => {
    setOpen(false)
  }

  return (
    <Box>
      <Button
        data-component="virtual-badge-button"
        sx={themeSx.button}
        onClick={openModal}
        startIcon={<Avatar sx={themeSx.avatar} src={avatar} alt={nick} />}
      >
        <Typography>{t('virtual-badge-show')}</Typography>
      </Button>
      <Modal open={open} onClose={closeModal} sx={themeSx.modal}>
        <Paper className="companion-virtual-badge-modal">
          <Box className="companion-virtual-badge-content">
            <Grid container spacing={2} alignItems="center" justifyContent="center">
              <Grid item xs={12}>
                <Box className="companion-virtual-badge-avatar-wrapper">
                  <Avatar className="companion-virtual-badge-avatar" src={avatar} alt={nick} />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography className="companion-virtual-badge-label">
                  {t('virtual-badge-nickname')}
                </Typography>
                <Typography>{nick}</Typography>
                <Typography className="companion-virtual-badge-label">
                  {t('virtual-badge-number')}
                </Typography>
                <Typography>{attendee}</Typography>
              </Grid>
            </Grid>
          </Box>
          <Box className="companion-virtual-badge-qr">
            {loading && (
              <Box className="companion-virtual-badge-qr-placeholder">
                <Skeleton variant="rectangular" />
              </Box>
            )}
            {!loading && isDefined(error) && (
              <Typography>{error}</Typography>
            )}
            {!loading && !isDefined(error) && isDefined(qrUrl) && (
              <img src={qrUrl} alt={t('virtual-badge-title')} />
            )}
          </Box>
        </Paper>
      </Modal>
    </Box>
  )
}
